import {useRef, useEffect, useState} from "react";
import "../styles/Chat.scss";
import chatPosterImage from "../assets/images/chat_poster.png";
import {ConnectionState} from "../../../hooks/socket/socket.type";
import {
  AudioContextError,
  AUDIO_CONTEXT_ERROR_CODES,
} from "src/hooks/audioContext/audioContext.error";
import {useChat} from "..";
import {useTypingEffect} from "../hooks/useTypingEffect";
import {
  RECORDING_MAX_TIME,
  RECORDING_WARNING_TIME,
} from "../assets/constants/chatOptions";
import sendMessageIcon from "../assets/images/chat_send_icon.png";
import {BackButton, Timer, Toast} from "../../../components";
import {Artist} from "src/utils/artist.utils";
import {useTranslation} from "react-i18next";
import micPermissionIcon from "../../../assets/images/common/mic_off_icon.png";
import {getCharacterFromStorage} from "../storage";

const CHAT_TIMER_WARNING_THRESHOLD_SECONDS = 30 * 60; // 30분
const WARNING_REMAINING_SECONDS = 10 * 60; // 10분

type ChatProps = {
  artist: Artist;
  onBack: () => void;
  onExpire: () => void;
};

const Chat = ({artist, onBack, onExpire}: ChatProps) => {
  const {t, i18n} = useTranslation("common");

  const selectedCharacter = getCharacterFromStorage();
  const character = selectedCharacter?.identifier ?? "rizuko";

  const {
    chatSocketConnect,
    messages,
    audioState,
    handleStartAudioRecording,
    handleStopAudioRecording,
    isProcessingAnswer,
    connectionState,
    socketEventError,
    clearSocketEventError,
    isVoiceActivated,
    activateVoice,
    recordingTimeRemaining,
    inputValue,
    handleInputChange,
    handleSubmitInput,
    remainingSeconds,
  } = useChat({
    artist,
    character,
    language: i18n.language,
    onExpire,
  });

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [showRemainingTimeToast, setShowRemainingTimeToast] = useState(false);
  const [showMicPermissionModal, setShowMicPermissionModal] = useState(false);
  const [recordingGuideBubblePhase, setRecordingGuideBubblePhase] = useState<
    "hidden" | "visible" | "exiting"
  >("hidden");

  const lastMessage = messages[messages.length - 1];
  const isLastAssistant = lastMessage?.role === "assistant";
  const completedMessages = isLastAssistant ? messages.slice(0, -1) : messages;
  const typingMessage = isLastAssistant ? lastMessage : null;
  const lastAssistantContent = typingMessage?.content ?? "";
  const displayedText = useTypingEffect(lastAssistantContent, {speedMs: 50});

  const handleInputClick = async () => {
    if (!isVoiceActivated) {
      try {
        await activateVoice();
      } catch (error) {
        console.error("Failed to activate audio:", error);
      }
    }
  };

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    setTimeout(() => {
      scrollToBottom();
    }, 0);
  }, [messages, isProcessingAnswer, displayedText]);

  useEffect(() => {
    if (!socketEventError) {
      return;
    }
    const timer = setTimeout(() => {
      clearSocketEventError();
    }, 3000);
    return () => clearTimeout(timer);
  }, [socketEventError, clearSocketEventError]);

  useEffect(() => {
    if (remainingSeconds !== WARNING_REMAINING_SECONDS) {
      return;
    }
    setShowRemainingTimeToast(true);
  }, [remainingSeconds]);

  useEffect(() => {
    if (audioState !== "recording") {
      setRecordingGuideBubblePhase((prev) =>
        prev !== "hidden" ? "exiting" : prev,
      );
      return;
    }
    setRecordingGuideBubblePhase("visible");
    const timer = window.setTimeout(() => {
      setRecordingGuideBubblePhase("exiting");
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [audioState]);

  const handleRecordingGuideBubbleAnimationEnd = (
    e: React.AnimationEvent<HTMLDivElement>,
  ) => {
    if (e.animationName === "fadeOut") {
      setRecordingGuideBubblePhase("hidden");
    }
  };

  return (
    <div className="chat-container">
      {socketEventError && (
        <Toast message={t("chat.errorOccurred")} position={{bottom: 84}} />
      )}
      {showRemainingTimeToast && (
        <Toast
          message={t("chat.timeRemainingPopup.notice")}
          position={{bottom: 84}}
        />
      )}
      <div className="chat-header">
        <BackButton artist={artist} onClick={onBack} />
        <Timer
          artist={artist}
          remainingSeconds={remainingSeconds}
          format="h:mm:ss"
          warningThresholdSeconds={CHAT_TIMER_WARNING_THRESHOLD_SECONDS}
        />
      </div>
      {/* 소켓 연결 실패 시 UI 표시 */}
      {connectionState === ConnectionState.DISCONNECTED && (
        <div className="chat-reconnect-banner">
          <p className="chat-reconnect-banner-text">
            연결이 원활하지 않아요! 네트워크를 확인해 주세요.
          </p>
          <button className="chat-reconnect-button" onClick={chatSocketConnect}>
            연결하기
          </button>
        </div>
      )}
      <div className="chat-wrapper">
        <div className="chat-background-container">
          <video
            autoPlay
            muted
            loop
            playsInline
            controls={false}
            disablePictureInPicture
            preload="metadata"
            poster={chatPosterImage}
            className="chat-background-video"
          >
            <source
              src="https://s3.ap-northeast-2.amazonaws.com/image.dev.eqbr.com/ai-kiosk/SM/assets/temp_background_720.mp4"
              type="video/mp4"
            />
          </video>
        </div>
        <div className="chat-messages-container" ref={messagesContainerRef}>
          <div className="chat-messages-spacer" />
          {/* 채팅 메세지 목록 표시 */}
          {completedMessages.map((message) => (
            <div
              key={message.ts}
              className={`chat-message ${message.role} ${artist}`}
            >
              <div className="chat-message-bubble">
                <p className="chat-message-text">{message.content}</p>
              </div>
            </div>
          ))}
          {typingMessage && (
            <div
              key={typingMessage.ts}
              className={`chat-message assistant ${artist}`}
            >
              <div className="chat-message-bubble">
                <p className="chat-message-text">{displayedText}</p>
              </div>
            </div>
          )}
          {/* 답변 처리 중 UI 표시 */}
          {isProcessingAnswer && (
            <div className={`chat-message assistant ${artist}`}>
              <div className="chat-message-bubble">
                <div className="chat-processing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div className={"chat-messages-bottom-spacer"} />
        </div>
        {/* 채팅 시작 안내 메시지 */}
        {messages.length === 0 && (
          <div className="chat-welcome-message-wrapper">
            <div
              className={`chat-message chat-welcome-message assistant ${artist}`}
            >
              <div className="chat-welcome-message-bubble">
                <p className="chat-message-text">{t("chat.greeting")}</p>
              </div>
            </div>
          </div>
        )}
        <div className="chat-input-container">
          <div className="chat-mic-button-wrapper">
            {recordingGuideBubblePhase !== "hidden" && (
              <div
                className={`chat-recording-guide-bubble ${recordingGuideBubblePhase === "exiting" ? "exiting" : ""}`}
                role="status"
                onAnimationEnd={handleRecordingGuideBubbleAnimationEnd}
              >
                <p className="chat-recording-guide-bubble-text">
                  {t("chat.recordingGuideBubble")}
                </p>
              </div>
            )}
            {audioState === "recording" && (
              <RecordingProgressRing
                timeRemaining={recordingTimeRemaining}
                artist={artist}
              />
            )}
            <button
              className={`chat-mic-button ${artist}`}
              onClick={() => {
                if (audioState === "recording") {
                  handleStopAudioRecording();
                }
                if (audioState === "idle") {
                  (async () => {
                    try {
                      await handleStartAudioRecording();
                    } catch (e) {
                      if (
                        e instanceof AudioContextError &&
                        e.code === AUDIO_CONTEXT_ERROR_CODES.NOT_ALLOWED
                      ) {
                        setShowMicPermissionModal(true);
                      }
                    }
                  })();
                }
              }}
              disabled={
                audioState === "processing" ||
                connectionState === ConnectionState.DISCONNECTED ||
                isProcessingAnswer ||
                inputValue.trim().length > 0
              }
            >
              <MicButtonIcon
                state={audioState as "recording" | "processing" | "idle"}
              />
            </button>
          </div>
          <div className="chat-input-wrapper">
            <input
              type="text"
              className="chat-input"
              placeholder={t("chat.inputPlaceholder")}
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSubmitInput();
                }
              }}
              onClick={handleInputClick}
              autoComplete="off"
              disabled={
                connectionState === ConnectionState.DISCONNECTED ||
                isProcessingAnswer
              }
            />
            <button
              className={`chat-send-message-button ${artist}`}
              onClick={handleSubmitInput}
              disabled={
                !inputValue.trim() ||
                connectionState === ConnectionState.DISCONNECTED ||
                isProcessingAnswer
              }
            >
              <img
                src={sendMessageIcon}
                alt=""
                className="chat-send-message-icon"
              />
            </button>
          </div>
        </div>
      </div>
      {showMicPermissionModal && (
        <div className="chat-mic-permission-overlay">
          <div className="chat-mic-permission-modal">
            <div className="chat-mic-permission-content">
              <div className="chat-mic-permission-icon-area">
                <img
                  src={micPermissionIcon}
                  alt=""
                  className="chat-mic-permission-icon"
                />
              </div>
              <p className="chat-mic-permission-title">
                {t("chat.micPermissionPopup.title")}
              </p>
              <div className="chat-mic-permission-description-wrapper">
                <p className="chat-mic-permission-description">
                  {t("chat.micPermissionPopup.description")}
                </p>
                <p className="chat-mic-permission-how-to-set">
                  {t("chat.micPermissionPopup.howToSet")}
                </p>
              </div>
            </div>
            <button
              type="button"
              className={`chat-mic-permission-confirm ${artist}`}
              onClick={() => setShowMicPermissionModal(false)}
            >
              {t("chat.micPermissionPopup.button")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;

const MicButtonIcon = ({state}: {state: string}) => {
  if (state === "recording") {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="4" y="4" width="16" height="16" fill="currentColor" rx="2" />
      </svg>
    );
  }

  if (state === "processing") {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="4" y="6" width="6" height="12" fill="currentColor" />
        <rect x="14" y="6" width="6" height="12" fill="currentColor" />
      </svg>
    );
  }

  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 1C10.34 1 9 2.34 9 4V12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12V4C15 2.34 13.66 1 12 1Z"
        fill="currentColor"
      />
      <path
        d="M19 10V12C19 15.87 15.87 19 12 19C8.13 19 5 15.87 5 12V10H7V12C7 14.76 9.24 17 12 17C14.76 17 17 14.76 17 12V10H19Z"
        fill="currentColor"
      />
      <path d="M11 22H13V20H11V22Z" fill="currentColor" />
    </svg>
  );
};

type RecordingProgressRingProps = {
  timeRemaining: number;
  artist: string;
};

const RecordingProgressRing = ({
  timeRemaining,
  artist,
}: RecordingProgressRingProps) => {
  const progress =
    ((RECORDING_MAX_TIME - timeRemaining) / RECORDING_MAX_TIME) * 100;
  const isWarning = timeRemaining <= RECORDING_WARNING_TIME;

  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progress / 100);

  return (
    <svg
      className={`chat-recording-progress-ring ${artist}`}
      width="64"
      height="64"
      viewBox="0 0 64 64"
    >
      <circle
        cx="32"
        cy="32"
        r={radius}
        fill="none"
        className="progress-ring-track"
        strokeWidth="3"
      />
      <circle
        cx="32"
        cy="32"
        r={radius}
        fill="none"
        className={isWarning ? "progress-ring-warning" : "progress-ring-normal"}
        strokeWidth="3"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        transform="rotate(-90 32 32)"
      />
    </svg>
  );
};
